"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import {
  Search,
  PlusCircle,
  Hammer,
  RefreshCw,
  Server,
  Box,
  CheckCircle2,
  AlertCircle,
  Menu,
  BookOpen,
} from "lucide-react";
import { useToast } from "../ui/Toast";
import { UserMenu } from "./UserMenu";
import { ThemeSelector } from "./ThemeSelector";
import { LanguageSelector } from "./LanguageSelector";
import { NotificationBell } from "../notifications/NotificationBell";
import { useCluster } from "@/lib/cluster/context";
import { useAuth } from "@/lib/auth/context";
import { isPlatformAdminRole } from "@/lib/auth/roles";
import { useLanguage } from "@/lib/i18n/context";

export function Header({
  onOpenCommandPalette,
  onOpenNavigation,
}: {
  onOpenCommandPalette?: () => void;
  onOpenNavigation?: () => void;
}) {
  const { clusters, selectedClusterId, setSelectedClusterId } = useCluster();
  const { user: authUser } = useAuth();
  const { t } = useLanguage();
  const isSuperAdmin = isPlatformAdminRole(authUser?.role);
  const [harborOnline, setHarborOnline] = useState<boolean | null>(null);
  const [reconciling, setReconciling] = useState(false);
  const toast = useToast();

  const fetchHarborStatus = async () => {
    if (!isSuperAdmin) return;
    try {
      const hRes = await fetch("/api/harbor");
      if (hRes.ok) {
        const hData = await hRes.json();
        const anyOnline = hData.registries?.some((r: { isOnline: boolean }) => r.isOnline);
        setHarborOnline(hData.registries?.length > 0 ? anyOnline : null);
      }
    } catch {
      // Ignore
    }
  };

  useEffect(() => {
    fetchHarborStatus();
  }, [isSuperAdmin]);

  const handleQuickReconcile = async () => {
    if (clusters.length === 0) {
      toast.warning(t("layout.header.noClustersAvailable"));
      return;
    }
    setReconciling(true);
    try {
      const targetClusterId =
        selectedClusterId && selectedClusterId !== "all"
          ? selectedClusterId
          : clusters.find((c) => c.isDefault)?.id || clusters[0].id;

      const res = await fetch("/api/reconciliation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clusterId: targetClusterId, action: "reconcile" }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(
          t("layout.header.reconcileSuccessTitle"),
          t("layout.header.reconcileSuccessMessage", {
            matched: data.report?.matchedSites ?? 0,
            drifted: data.report?.driftedSites ?? 0,
          })
        );
      } else {
        toast.error(t("layout.header.reconcileError"), data.error);
      }
    } catch {
      toast.error(t("layout.header.reconcileServiceError"));
    } finally {
      setReconciling(false);
    }
  };

  return (
    <header className="min-h-16 bg-surface-header border-b border-themed-border px-4 sm:px-6 py-2 flex flex-wrap items-center justify-between gap-3 sticky top-0 z-30 shadow-sm backdrop-blur-md transition-colors">
      {/* Search / Command Palette Trigger */}
      <div className="flex items-center gap-3 flex-1 min-w-[8rem] max-w-md">
        <button
          type="button"
          onClick={onOpenNavigation}
          aria-label="Open navigation"
          className="lg:hidden p-2 rounded-xl bg-surface-muted border border-themed-border text-themed-textSecondary hover:text-themed-textPrimary hover:border-themed-borderAccent transition-colors"
        >
          <Menu className="w-4 h-4" />
        </button>
        <button
          onClick={onOpenCommandPalette}
          className="w-full min-w-0 flex items-center justify-between gap-2 px-3.5 py-2 rounded-xl bg-surface-input border border-themed-border text-themed-textMuted hover:text-themed-textPrimary hover:border-themed-borderAccent transition-all text-xs shadow-sm"
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <Search className="w-4 h-4 shrink-0 text-[var(--accent-primary)]" />
            <span className="font-medium text-themed-textMuted truncate">{t(isSuperAdmin ? "layout.header.searchPlaceholder" : "portal.tools.search")}</span>
          </div>
          <kbd className="hidden shrink-0 sm:inline-block px-1.5 py-0.5 text-[10px] font-mono bg-surface-muted border border-themed-border rounded-md text-themed-textSecondary font-semibold shadow-sm">
            {t("layout.header.ctrlK")}
          </kbd>
        </button>
      </div>

      {/* Right Controls */}
      <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2 sm:gap-2.5">
        {/* Admin-only controls */}
        {isSuperAdmin && (
          <>
            {/* Active Cluster Switcher */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-surface-muted border border-themed-border text-xs shadow-sm">
              <Server className="w-3.5 h-3.5 text-[var(--accent-primary)] shrink-0" />
              <select
                value={selectedClusterId}
                onChange={(e) => setSelectedClusterId(e.target.value)}
                className="max-w-40 bg-transparent text-themed-textPrimary text-xs focus:outline-none cursor-pointer font-semibold font-mono"
              >
                <option value="all" className="bg-surface-card text-themed-textPrimary font-bold">
                  🌐 {t("layout.header.allClusters")} ({clusters.length})
                </option>
                {clusters.map((c) => (
                  <option key={c.id} value={c.id} className="bg-surface-card text-themed-textPrimary">
                    {c.name} {c.isK3s ? "(K3s)" : ""} {c.status === "READY" ? "● Ready" : "○ Offline"}
                  </option>
                ))}
              </select>
            </div>

            {/* Harbor Status Badge */}
            <Link
              href="/harbor"
              className={`hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all ${
                harborOnline === true
                  ? "bg-status-success-bg border-status-success-border text-status-success-text hover:opacity-90 shadow-sm"
                  : harborOnline === false
                  ? "bg-status-danger-bg border-status-danger-border text-status-danger-text hover:opacity-90 shadow-sm"
                  : "bg-surface-muted border-themed-border text-themed-textMuted hover:bg-surface-cardHover"
              }`}
            >
              <Box className="w-3.5 h-3.5" />
              <span className="sr-only 2xl:not-sr-only">{t("layout.header.harbor")}</span>
              {harborOnline === true && <CheckCircle2 className="w-3 h-3 text-status-success" />}
              {harborOnline === false && <AlertCircle className="w-3 h-3 text-status-danger" />}
            </Link>

            {/* Reconcile Button */}
            <button
              onClick={handleQuickReconcile}
              disabled={reconciling}
              title={t("layout.header.reconcileTitle")}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-surface-muted hover:bg-surface-cardHover text-themed-textPrimary border border-themed-border text-xs font-semibold transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-status-info ${reconciling ? "animate-spin" : ""}`} />
              <span className="sr-only 2xl:not-sr-only">{t("layout.header.reconcile")}</span>
            </button>

            {/* Build Image Button */}
            <Link
              href="/builds"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-surface-muted hover:bg-surface-cardHover text-themed-textPrimary border border-themed-border text-xs font-semibold transition-all"
            >
              <Hammer className="w-3.5 h-3.5 text-status-warning" />
              <span className="sr-only 2xl:not-sr-only">{t("layout.header.build")}</span>
            </Link>

            {/* Wiki Documentation Button */}
            <Link
              href="/wiki"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-surface-muted hover:bg-surface-cardHover text-themed-textPrimary border border-themed-border text-xs font-semibold transition-all hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]"
            >
              <BookOpen className="w-3.5 h-3.5 text-[var(--accent-primary)]" />
              <span className="sr-only 2xl:not-sr-only">{t("layout.header.wikiDoc")}</span>
            </Link>

            {/* New Site Primary Action */}
            <Link
              href="/sites/new"
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-secondary)] hover:opacity-95 text-[var(--accent-text)] font-black text-xs shadow-md shadow-[var(--accent-glow)] transition-all"
            >
              <PlusCircle className="w-4 h-4 text-[var(--accent-text)] stroke-[2.5]" />
              <span className="sr-only sm:not-sr-only">{t("layout.header.newSite")}</span>
            </Link>
          </>
        )}

        {/* Notifications */}
        {authUser && <NotificationBell />}

        {/* Language Selector */}
        <LanguageSelector />

        {/* Theme Selector */}
        <ThemeSelector />

        {/* User Menu & Authentication */}
        <div className="pl-1 border-l border-themed-borderSubtle">
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
